import { AlertTriangle, Inbox, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export const PageHeader = ({ title, subtitle, actions, testid }) => (
  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8 pf-fade" data-testid={testid || "page-header"}>
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="text-sm sm:text-base text-muted-foreground mt-1.5 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const StatCard = ({ label, value, hint, icon: Icon, tone = "default", onClick, testid }) => {
  const tones = { default: "text-slate-900", good: "text-primary", warn: "text-amber-700", bad: "text-red-600", blue: "text-secondary" };
  return (
    <button type="button" onClick={onClick} disabled={!onClick} data-testid={testid}
      className={cn("pf-card p-5 text-left w-full group transition-[box-shadow,transform] duration-200", onClick && "hover:shadow-md hover:-translate-y-0.5 cursor-pointer")}>
      <div className="flex items-start justify-between gap-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        {Icon && <span className="h-9 w-9 rounded-lg bg-accent grid place-items-center text-primary"><Icon size={18} /></span>}
      </div>
      <div className={cn("mt-3 text-2xl font-bold font-display pf-num", tones[tone])}>{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </button>
  );
};

export const EmptyState = ({ title = "Nothing here yet", text, action, testid = "empty-state" }) => (
  <div className="flex flex-col items-center justify-center text-center py-14 px-6" data-testid={testid}>
    <div className="h-12 w-12 rounded-full bg-muted grid place-items-center mb-4"><Inbox className="text-muted-foreground" size={22} /></div>
    <p className="font-semibold text-slate-800">{title}</p>
    {text && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const ErrorState = ({ error, onRetry }) => (
  <div className="pf-card p-6 flex items-start gap-4 border-red-200 bg-red-50/60" data-testid="error-state">
    <AlertTriangle className="text-red-600 shrink-0" />
    <div className="flex-1">
      <p className="font-semibold text-red-800">Could not load data</p>
      <p className="text-sm text-red-700 mt-1">{error}</p>
    </div>
    {onRetry && <Button variant="outline" size="sm" onClick={onRetry} data-testid="retry-button"><RefreshCw size={14} className="mr-1.5" />Retry</Button>}
  </div>
);

export const TableSkeleton = ({ rows = 6 }) => (
  <div className="space-y-2 p-4" data-testid="loading-skeleton">
    {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
  </div>
);

const TONE = {
  green: "bg-emerald-50 text-emerald-800 border-emerald-200", blue: "bg-blue-50 text-blue-800 border-blue-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200", red: "bg-red-50 text-red-700 border-red-200",
  gray: "bg-slate-100 text-slate-700 border-slate-200", gold: "bg-yellow-50 text-yellow-800 border-yellow-300", navy: "bg-slate-900 text-white border-slate-900",
};
export const Pill = ({ tone = "gray", children, className, testid }) => (
  <span data-testid={testid} className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap", TONE[tone], className)}>{children}</span>
);

export const ExpiryPill = ({ days, date }) => {
  const tone = days <= 0 ? "red" : days <= 30 ? "red" : days <= 90 ? "gold" : "green";
  return <Pill tone={tone}>{days <= 0 ? "Expired" : `${days}d`} · {date}</Pill>;
};

export const daysUntil = (iso) => Math.round((new Date(iso + "T00:00:00") - new Date(new Date().toLocaleDateString("en-CA", { timeZone: "Africa/Accra" }) + "T00:00:00")) / 86400000);

export const Pager = ({ page, total, size, onPage }) => {
  const pages = Math.max(1, Math.ceil((total || 0) / size));
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t text-sm" data-testid="pager">
      <span className="text-muted-foreground">{total} records · page {page} of {pages}</span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="pager-prev">Previous</Button>
        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="pager-next">Next</Button>
      </div>
    </div>
  );
};

export const Field = ({ label, error, children, hint }) => (
  <label className="block space-y-1.5">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    {children}
    {hint && !error && <span className="block text-xs text-muted-foreground">{hint}</span>}
    {error && <span className="block text-xs text-red-600">{error}</span>}
  </label>
);

export const DemoBadge = ({ children = "Demo / synthetic data" }) => <Pill tone="gold" testid="demo-badge">{children}</Pill>;
